<template>
  <div class="new_chart_container">
    <ChartHeader
      v-if="showHeader"
      :showDuration="showDuration"
      :stockInfo="stockInfo"
      @handleSelectedFilter="filterChartData($event)"
      :isCreateView="isCreateView"
    />
    <apexchart
      ref="chart"
      v-if="data && data.length"
      type="line"
      :options="options"
      :series="seriesData"
      height="100%"
      width="100%"
    ></apexchart>
    <!-- <Skeletor width="100%" style="margin-top: 15px" height="195px" v-else /> -->
  </div>
</template>
<script lang="ts">
import ChartHeaderModel from "../../models/chartHeader.model";
import { GeneralStockModel } from "../../models/generalstock.model";
import { StockMarkets } from "../../models/stocks.model";
import firebaseService from "@/services/firebase.service";
import httpService from "@/services/http.service";
import utilService from "@/services/util.service";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import VueApexCharts from "vue3-apexcharts";
import IconComponent from "@/components/IconComponent.vue";
import ChartHeader from "./chartHeader.vue";
import filters from "@/helpers/filters";
import moment from "moment";

export default defineComponent({
  components: {
    apexchart: VueApexCharts,
    IconComponent: IconComponent,
    ChartHeader,
  },
  props: {
    stock: {
      type: Object,
    },
    widget: {
      type: Object,
    },
    backgroundColor: {
      type: String,
      default: filters.getComputedVariable("--card-inner-color-profile"),
    },
    showGrid: {
      default: false,
    },
    isPreview: {
      default: false,
    },
    randomColors: {
      default: false,
    },
    showDuration: {
      type: Boolean,
      default: true,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showDaysFormat: {
      type: Boolean,
      default: false,
    },
    showLabels: {
      type: Boolean,
      default: true,
    },
    colorsArr: {
      type: Object,
      default: null,
    },
    legends: {
      type: Boolean,
      default: true,
    },
    isDataIndirect: {
      type: Boolean,
      default: true,
    },
    showYAxis: {
      type: Boolean,
      default: true,
    },
    layoutPortfolio: {
      type: Boolean,
      default: false,
    },
    isCreateView: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    let data = ref<any[]>([]);
    let stockInfo = ref<ChartHeaderModel>();
    let timeout: any;

    // const seriesData = computed(() => {
    //   return utilService.generateSeriesForChart(data.value ?? []);
    // });
    const seriesData = computed(() => {
      return utilService
        .generateSeriesForChart(data.value)
        .map((dataObject) => {
          if (Array.isArray(dataObject?.data)) {
            const sortedData = [...dataObject.data].sort(
              (a, b) => moment(a.x).valueOf() - moment(b.x).valueOf()
            );
            const times = sortedData.map((row) => moment(row.x));
            const minTime = moment.min(times);
            const maxTime = moment.max(times);
            const hoursDiff = maxTime.diff(minTime, "hours");
            const formatString = hoursDiff <= 24 ? "HH:mm:ss" : "MM/DD";

            return {
              ...dataObject,
              data: sortedData.map((row) => ({
                x: moment(row.x).format(formatString),
                y: row.y,
              })),
            };
          }
          return dataObject;
        });
    });

    let chart = ref();

    const getColor = () => {
      if (props.isPreview) {
        var index = Math.floor(Math.random() * 12);
        return utilService.chartColors[index];
      } else {
        return filters.getComputedVariable("--text-alt-color");
      }
    };
    var options = computed(() => {
      const totalLabels = seriesData.value?.[0]?.data?.length || 0;

      const optionsData = {
        chart: {
          id: "line-chart",
          type: "line",
          fontFamily: "Roboto, Arial, sans-serif",
          animations: {
            enabled: false,
            easing: "easeout",
            speed: 200,
          },

          // redrawOnWindowResize: true,
          // redrawOnParentResize: true,
          // selection: {
          //   enabled: false,
          // },
          toolbar: {
            show: false,
            tools: {
              download: false,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
              customIcons: [],
            },
            export: {
              csv: {
                filename: undefined,
                columnDelimiter: ",",
                headerCategory: "category",
                headerValue: "value",
              },
              svg: {
                filename: undefined,
              },
              png: {
                filename: undefined,
              },
            },
            autoSelected: "zoom",
          },
        },
        grid: {
          show: props.showGrid,
          borderColor: filters.getComputedVariable("--chart-legend"),
        },
        dataLabels: {
          enabled: false,
        },
        markers: {
          size: 0,
          style: "hollow",
        },
        xaxis: {
          type: "category",
          // tickAmount: "dataPoints",
          tickAmount: totalLabels > 30 ? 30 : totalLabels, // max 60 ticks

          labels: {
            show: props.showLabels,
            // trim: true,
            // rotate: -45,
            // rotateAlways: true,
            // minHeight: 100,
            // hideOverlappingLabels: false,
            // showDuplicates: false,
            style: {
              colors: filters.getComputedVariable("--chart-legend"),
              fontSize: "11px",
              fontWeight: 500,
              cssClass: "apexcharts-xaxis-label",
            },
          //   offsetX: -30,
          //   offsetY: 25,
            // formatter: (value: any) => {
            //   var date = new Date(value);
            //   if (props.showDaysFormat) {
            //     return moment(date).format("DD MMM");
            //   } else {
            //     return utilService.convert24HoursTo12Hours(date);
            //   }
            // },
            datetimeFormatter: {
              year: "yyyy",
              month: "M/yy",
              day: "dd/M",
              hour: "hh tt",
            },
          },
          axisBorder: {
            show: props.showLabels,
            color: filters.getComputedVariable("--chart-legend"),
            width: "100%",
          },
          axisTicks: {
            show: props.showLabels,
            borderType: "solid",
            color: filters.getComputedVariable("--chart-legend"),
          },
        },
        yaxis: {
          show: false,
        } as any,
        stroke: {
          curve: "smooth",
          width: 4,
        },
        tooltip: {
          shared: false,
          x: {
            format: "dd MMM",
          },
          y: {
            formatter: (name: any) => name,
          },
        },
        legend: {
          show: true,
          labels: {
            colors: [filters.getComputedVariable("--text-color")],
          },
        },
        colors: props.colorsArr ?? [getColor(), filters.getComputedVariable("--text-color"), "#00fff6"],
      }
      if (props.showYAxis) {
        optionsData.yaxis = {
          show: props.legends,
          maxWidth: 20,
          labels: {
            align: "left",
            style: {
              colors: filters.getComputedVariable("--chart-legend"),
              fontSize: "11px",
            },
            formatter: function (value: any) {
              return filters.systemNumberConventionOld(value, true);
            },
          },
          axisBorder: {
            show: props.legends,
            color: filters.getComputedVariable("--chart-legend"),
            width: 2,
          },
          axisTicks: {
            show: props.legends,
            color: filters.getComputedVariable("--chart-legend"),
            borderType: "solid",
            width: 4,
          },
        };
      }
      return optionsData;
    })

    watch(
      () => props,
      () => {
        if (
          props.isDataIndirect &&
          stockInfo.value &&
          props.widget &&
          props.widget!.customData &&
          props.widget!.customData.stock &&
          stockInfo.value.symbol != props.widget!.customData.stock.symbol
        ) {
          data.value = [];
          stockInfo.value = undefined;
          updateChartData();
        } else if (props.widget!.customData.chartData) {
          stockInfo.value = GeneralStockModel.getChartHeader(
            props.widget!.customData.stock
          );
          data.value = props.widget!.customData.chartData;
        }
      },
      { deep: true }
    );

    onBeforeUnmount(() => {
      if (timeout) {
        clearTimeout(timeout);
      }
    });

    onMounted(async () => {
      // if (props.showYAxis) {
      //   options.value.yaxis = {
      //     show: props.legends,
      //     maxWidth: 20,
      //     labels: {
      //       align: "left",
      //       style: {
      //         colors: filters.getComputedVariable("--chart-legend"),
      //         fontSize: "11px",
      //       },
      //       formatter: function (value: any) {
      //         return filters.systemNumberConventionOld(value, true);
      //       },
      //     },
      //     axisBorder: {
      //       show: props.legends,
      //       color: filters.getComputedVariable("--chart-legend"),
      //       width: 2,
      //     },
      //     axisTicks: {
      //       show: props.legends,
      //       color: filters.getComputedVariable("--chart-legend"),
      //       borderType: "solid",
      //       width: 4,
      //     },
      //   };
      // }
      // if (props.layoutPortfolio) {
      //   options.value.chart.sparkline = {
      //     enabled: true,
      //   };
      //   options.value.stroke = {
      //     width: 1,
      //   };
      //   options.value.fill = {
      //     gradient: {
      //       enabled: true,
      //       opacityFrom: 0.5,
      //       opacityTo: 0,
      //     },
      //   };
      //   options.value.color = ["#4db4fa"];
      // }

      if (props.widget) {
        try {
          if (props.isDataIndirect && !props.widget!.customData.chartData) {
            updateChartData();
          } else {
            stockInfo.value = GeneralStockModel.getChartHeader(
              props.widget!.customData.stock
            );
            data.value = props.widget!.customData.chartData;
            window.dispatchEvent(new Event("resize"));
          }
        } catch (err) {
        }
      }
    });

    const getStocks = async () => {
      let response = httpService
        .getstocks(props.widget!.customData.stock.symbol)
        .then((response) => {
          if (response.data.isSuccess) {
            stockInfo.value = GeneralStockModel.getChartHeader(
              response.data.data[0]
            );
          }
        });
    };

    const getCryptos = async () => {
      let response = httpService
        .getCryptos(props.widget!.customData.stock.symbol)
        .then((response) => {
          if (response.data.isSuccess) {
            stockInfo.value = GeneralStockModel.getChartHeader(
              response.data.data[0]
            );
          }
        });
    };

    const updateChartData = async (isFirst: boolean = true) => {
      if (props.widget!.customData.stock) {
        if (isFirst) {
          if (props.widget!.customData.stock.symbol!) {
            firebaseService
              .getChartHeader(props.widget!.customData.stock.symbol!)
              .then((val) => {
                if (val) {
                  stockInfo.value = val;
                }
              });
          }
          firebaseService
            .getStockChart(
              props.widget!.customData.stock.symbol!,
              props.widget!.customData.duration,
              props.widget!.customData.interval,
              props.widget!.styleType,
              props.widget!.customData.dataType
            )
            .then((arr: any[]) => {
              data.value = arr;
              getLineChartData();
            });
        } else {
          if (!stockInfo.value) {
            firebaseService
              .getChartHeader(props.widget!.customData.stock.symbol!)
              .then((val) => {
                if (val) {
                  stockInfo.value = val;
                }
              });
          }
          getLineChartData();
        }
        if (!stockInfo.value || isFirst) {
          if (props.widget!.customData.stock.type == StockMarkets.stocks) {
            getStocks();
          } else {
            getCryptos();
          }
        }
      }
    };

    const getLineChartData = async () => {
      if (
        props.widget!.customData &&
        props.widget!.customData.dataType &&
        props.widget!.customData.dataType.length
      ) {
        var apiUrl = utilService.getChartType(
          "line",
          props.widget!.customData.stock.type
        )?.apiUrl!;
        if (props.widget && props.widget.apiUrl && !apiUrl) {
          apiUrl = props.widget.apiUrl;
        }
        apiUrl =
          apiUrl +
          utilService.getChartQuery(
            props.widget!.customData.stock.symbol!,
            props.widget!.customData.duration,
            props.widget!.customData.interval,
            props.widget!.customData.dataType,
            props.widget!.customData.stock.type,
            props.widget!.customData.stock.stockId
          );
        const res = httpService.getData(apiUrl).then((res) => {
          if (res.data.isSuccess) {
            data.value = res.data.data;
          }
          if (!props.isPreview) {
            timeout = setTimeout(async () => {
              updateChartData(false);
            }, utilService.liveUpdateDuration);
          }
        });
      }
    };

    const filterChartData = (filter: any) => {
      props.widget!.customData.duration = filter;
      props.widget!.customData.interval = "1h";
      getLineChartData();
      let a = data.value[0];
      chart.value.zoomX(
        new Date(a.data[0].x).getTime(),
        new Date(a.data[a.data.length - 1].x).getTime()
      );
    };
    return {
      chart,
      seriesData,
      data,
      options,
      stockInfo,
      filterChartData,
    };
  },
});
</script>
<style lang="scss" scoped>
.apexcharts-legend {
  padding: 0 !important;
}

.new_chart_container {
  height: calc(100% - 45px);
  width: 100%;
  display: flex;
  flex-flow: column;
  padding: 0 15px;
  .vue-apexcharts {
    margin: 0 -5px 0 -15px;
    // margin: 0 -10px;
  }

  &.widgets_chart {
    height: calc(100% - var(--custom-margin) - 30px);
  }
}

.chart {
  height: 100%;
  width: 100%;
  max-height: calc(100% - 60px);  
}

</style>
